import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Flex, 
  Heading, 
  useColorModeValue,
  Icon,
  Text 
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import CategoryTree from '../components/CategoryTree';
import CategoryModals from '../components/CategoryModals';
import api from '../api';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', parent: '' });

  // Define color mode values for consistent styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');

  // Fetch categories from the API
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/hierarchy/');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Toggle the expanded/collapsed state of a category
  const toggleCategory = (id) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleCreate = async () => {
    try {
      const payload = { name: formData.name };
      if (formData.parent) payload.parent = formData.parent;

      await api.post('/categories/', payload);
      fetchCategories();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create category:', error);
    }
  };

  const handleEdit = async () => {
    try {
      const payload = { name: formData.name };
      if (formData.parent) payload.parent = formData.parent;

      await api.put(`/categories/${selectedCategory.id}/`, payload);
      fetchCategories();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to edit category:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/categories/${id}/`);
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const handleCloseCreate = () => {
    setFormData({ name: '', parent: '' }); // Clear form data
    setIsCreateModalOpen(false);
  };
  
  const handleCloseEdit = () => {
    setFormData({ name: '', parent: '' }); // Clear form data
    setSelectedCategory(null); // Reset selected category
  };

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}> 
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Page Header - Modern Card Style */}
        <Box 
          position="relative"
          bg={cardBg} 
          p={{ base: 3, md: 8 }}
          borderRadius={{ base: 'lg', md: '2xl' }}
          mb={{ base: 3, md: 6 }}
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
        >
          {/* Decorative gradient overlay - Purple theme for categories */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #8B5CF6, #7C3AED, #6D28D9)"
          />
          {/* Header section with icon and title */}
          <Flex align="center" justify="space-between" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }}>
            <Flex align="center" gap={2}>
              <Box
                p={3}
                bg="linear-gradient(135deg, #8B5CF6, #7C3AED)"
                borderRadius="lg"
                mr={4}
                boxShadow="0 4px 6px -1px rgba(139, 92, 246, 0.3)"
              >
                <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                  <path fill="currentColor" d="M12 2l3.09 6.26L22 9l-5 4.87 1.18 6.88L12 17.77l-6.18 3.98L7 14.87 2 10l6.91-1.74L12 2z" opacity="0.4"/>
                  <path fill="currentColor" d="M4 4h4v4H4V4zm6 0h4v4h-4V4zm6 0h4v4h-4V4zM4 10h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4zM4 16h4v4H4v-4zm6 0h4v4h-4v-4z"/>
                </Icon>
              </Box>
              <Box>
                <Text 
                  fontSize={{ base: 'xl', md: '2xl' }}
                  fontWeight="bold"
                  bgGradient="linear(to-r, #8B5CF6, #7C3AED)"
                  bgClip="text"
                  mb={1}
                >
                  Категорії
                </Text>
                <Text 
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color={subtextColor}
                  fontWeight="medium"
                >
                  Керування структурою категорій
                </Text>
              </Box>
            </Flex>
            {/* Add Category Button with enhanced styling */}
            <Button 
              leftIcon={<AddIcon />} 
              bg="linear-gradient(135deg, #8B5CF6, #7C3AED)"
              color="white"
              size="md"
              borderRadius="xl"
              px={{ base: 4, md: 6 }}
              py={{ base: 2, md: 3 }}
              boxShadow="0 4px 6px -1px rgba(139, 92, 246, 0.3)"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "0 8px 15px -3px rgba(139, 92, 246, 0.4)",
                transition: "all 0.2s ease"
              }}
              _active={{
                transform: "translateY(0)",
              }}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Додати категорію
            </Button>
          </Flex>
        </Box>
        {/* Categories Content - Modern Card Style */}
        <Box 
          position="relative"
          bg={cardBg} 
          p={{ base: 2, md: 8 }}
          borderRadius={{base:'lg', md:'2xl'}} 
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor={borderColor}
          _hover={{
            transform: { base: 'none', md: 'translateY(-2px)' },
            boxShadow: { base: 'none', md: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
            transition: 'all 0.3s ease',
          }}
          overflow="hidden"
          mt={0}
        >
          {/* Decorative gradient overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #06B6D4, #0891B2, #0E7490)"
          />
          {/* Content Header */}
          <Flex align="center" mb={{base:2, md:6}} gap={{base:2, md:4}}>
            <Box
              p={3}
              bg="linear-gradient(135deg, #06B6D4, #0891B2)"
              borderRadius="lg"
              mr={4}
              boxShadow="0 4px 6px -1px rgba(6, 182, 212, 0.3)"
            >
              <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                <path fill="currentColor" d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
              </Icon>
            </Box>
            <Box>
              <Text 
                fontSize={{base:'md',md:'xl'}} 
                fontWeight="bold"
                bgGradient="linear(to-r, #06B6D4, #0891B2)"
                bgClip="text"
                mb={1}
              >
                Дерево категорій
              </Text>
              <Text 
                fontSize={{base:'xs',md:'sm'}} 
                color={subtextColor}
                fontWeight="medium"
              >
                Ієрархічна структура всіх категорій
              </Text>
            </Box>
          </Flex>
          {/* Category Tree Container */}
          <Box 
            bg={useColorModeValue('gray.50', 'gray.700')}
            borderRadius="xl"
            p={{ base: 2, md: 6 }}
            border="1px solid"
            borderColor={borderColor}
            minH="350px"
            overflowX="auto"
          >
            <CategoryTree
              categories={categories}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              onEdit={(category) => {
                setSelectedCategory(category);
                setFormData({ name: category.name, parent: category.parent || '' });
                setIsEditModalOpen(true);
              }}
              onDelete={handleDelete}
            />
          </Box>
        </Box>
        {/* Modals */}
        <CategoryModals
          isCreateModalOpen={isCreateModalOpen}
          isEditModalOpen={!!selectedCategory}
          onCloseCreate={handleCloseCreate}
          onCloseEdit={handleCloseEdit}
          onSubmitCreate={handleCreate}
          onSubmitEdit={handleEdit}
          categories={categories}
          formData={formData}
          setFormData={setFormData}
        />
      </Box>
    </Box>
  );
};

export default CategoriesPage;
